import { answerLines, hasAnything, isOpen, type Homework, type HomeworkAnswer, type HomeworkField, type HomeworkValue } from '@adhd/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { formatUntil } from '../components/KeepInMind.js';
import { HomeworkFieldList } from '../components/HomeworkFields.js';
import { RatingSlider } from '../components/RatingSlider.js';
import { useAi } from '../lib/ai.js';
import { api, ApiError } from '../lib/api.js';

/**
 * One question he was set: writing for it, and everything written so far.
 *
 * NOTHING HERE LEAVES. No share link has a grant that reaches homework, and the
 * smoke suite pins that the hard way — an Everything link's entire payload is
 * byte-identical whether or not any of this exists. The reason is in migration
 * 034: his therapist set him a task about somebody who holds a link to this
 * app, and `journalCount` is granted at the narrowest rung, so a homework kept
 * as journal entries would have announced itself in the SHAPE of his writing
 * even with every word withheld.
 *
 * WHAT THIS SCREEN REFUSES TO SAY. How many days he missed. How many days are
 * left as a fraction. Anything that reads as a score. "You have written on 5
 * days" is a record; "5 of 14" is a grade, and a reader subtracts.
 */
export function HomeworkPage(): JSX.Element {
  const { id = '' } = useParams();
  const { canReflect } = useAi();
  const [hw, setHw] = useState<Homework | null>(null);
  const [answers, setAnswers] = useState<HomeworkAnswer[]>([]);
  const [today, setToday] = useState('');
  const [draft, setDraft] = useState<Record<string, HomeworkValue>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  /*
   * Changing it lives HERE, on the question's own page, because this is where
   * he already comes to answer it. It was nowhere at all to begin with: the
   * only entry point was at the foot of the day fold, which shows the RUNNING
   * one rather than a way to change it — so "where do I edit this?" had no
   * answer. His words: "i dont see where the interface is to enable this
   * homework? or edit / change it".
   */
  const [editing, setEditing] = useState(false);
  const [readback, setReadback] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editFields, setEditFields] = useState<HomeworkField[]>([]);

  const load = useCallback(async () => {
    try {
      const [one, all] = await Promise.all([api.homeworkOne(id), api.homework()]);
      setHw(one.homework);
      setAnswers(one.answers);
      setToday(all.today);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load that.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (!hasAnything(draft)) return;
    setSaving(true);
    setError(null);
    try {
      await api.answerHomework(id, { values: draft });
      setDraft({});
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  }

  async function readBack(): Promise<void> {
    setReading(true);
    setError(null);
    try {
      const r = await api.readBackHomework(id);
      setReadback(r.text);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't write that up.");
    } finally {
      setReading(false);
    }
  }

  async function saveEdit(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await api.updateHomework(id, { title: editTitle.trim(), fields: editFields });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  }

  async function stop(): Promise<void> {
    if (!confirm('Stop asking? Everything you have written stays.')) return;
    setStopping(true);
    try {
      await api.stopHomework(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't stop it.");
    } finally {
      setStopping(false);
    }
  }

  if (!hw) {
    return (
      <div className="screen stack">
        <BackLink />
        {error ? <div className="banner banner-error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    );
  }

  const running = isOpen(hw, today || hw.startedOn);

  return (
    <div className="screen stack">
      <BackLink />

      <header className="stack-sm stack">
        <h1 className="reading">{hw.title}</h1>
        <p className="faint" style={{ margin: 0 }}>
          {running ? `Until ${formatUntil(hw.endsOn)}` : `Finished ${formatUntil(hw.endsOn)}`}
          {answers.length > 0 && ` · you have written on ${hw.answeredDays} ${hw.answeredDays === 1 ? 'day' : 'days'}`}
        </p>
      </header>

      {error && <div className="banner banner-error">{error}</div>}

      {running && (
        <section className="stack-sm stack">
          {hw.fields.map((f) => (
            <AnswerField
              key={f.id}
              field={f}
              value={draft[f.id] ?? null}
              onChange={(v) => setDraft((d) => ({ ...d, [f.id]: v }))}
            />
          ))}
          <button
            className="btn btn-primary btn-block"
            disabled={saving || !hasAnything(draft)}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save it'}
          </button>
          {/* The count a therapist asked for is never enforced. Leaving one
              empty is a saved answer, and nothing anywhere says which. */}
          <p className="faint" style={{ margin: 0 }}>
            Whatever you put down is enough. Empty boxes are not mentioned again.
          </p>
        </section>
      )}

      {answers.length > 0 && (
        <section className="stack-sm stack">
          {answers.map((a) => (
            <article key={a.id} className="card stack-sm stack">
              <span className="faint">{formatUntil(a.day)}</span>
              {/* answerLines, so a box he has since taken off still shows what
                  he wrote through it. Mapping the current fields made a
                  fortnight of writing vanish the moment he edited the form. */}
              {answerLines(hw.fields, a.values).map((line, i) => (
                <div key={i}>
                  {line.label !== null && hw.fields.length > 1 && (
                    <span className="faint">{line.label}</span>
                  )}
                  <p className="reading" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {line.text}
                  </p>
                </div>
              ))}
            </article>
          ))}
        </section>
      )}

      {/* Not "you haven't done it". A day with nothing on it is a day, and this
          screen has no opinion about it. */}
      {answers.length === 0 && <p className="muted">Nothing written for this yet.</p>}

      {answers.length > 0 && (
        <Link className="btn btn-secondary btn-block" to={`/homework/${id}/sheet`}>
          Print or save this
        </Link>
      )}

      {/* Quiet, and only when asked. Nothing offers this when the fortnight
          ends — an app that volunteers to summarise your homework is sending a
          notification with extra steps. */}
      {answers.length > 0 && canReflect && !readback && (
        <button className="btn btn-ghost btn-block" disabled={reading} onClick={() => void readBack()}>
          {reading ? 'Reading it back…' : 'Read it back to me'}
        </button>
      )}

      {readback && (
        <section className="card stack-sm stack">
          <span className="faint">For the appointment — not saved anywhere</span>
          <p className="reading" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {readback}
          </p>
          <button className="btn btn-ghost btn-compact" onClick={() => setReadback(null)}>
            Close
          </button>
        </section>
      )}

      {!running && (
        <Link className="btn btn-secondary btn-block" to="/homework/new">
          Start something else
        </Link>
      )}

      {running && !editing && (
        <button
          className="btn btn-secondary btn-block"
          onClick={() => {
            setEditTitle(hw.title);
            setEditFields(hw.fields);
            setEditing(true);
          }}
        >
          Change what it asks
        </button>
      )}

      {running && editing && (
        <section className="stack-sm stack">
          <div className="field">
            <label htmlFor="hw-edit-title">What you want to be asked</label>
            <textarea
              id="hw-edit-title"
              className="input"
              rows={2}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
          </div>
          <HomeworkFieldList fields={editFields} onChange={setEditFields} />
          <button
            className="btn btn-primary btn-block"
            disabled={saving || editTitle.trim().length === 0}
            onClick={() => void saveEdit()}
          >
            Save changes
          </button>
          <button className="btn btn-ghost btn-block" onClick={() => setEditing(false)}>
            Leave it as it is
          </button>
          <p className="faint" style={{ margin: 0 }}>
            Changing the boxes does not touch anything you have already written.
          </p>
        </section>
      )}

      {running && !editing && (
        <section className="stack-sm stack">
          <button className="btn btn-ghost btn-block" disabled={stopping} onClick={() => void stop()}>
            {stopping ? 'Stopping…' : 'Stop asking'}
          </button>
          <p className="faint" style={{ margin: 0 }}>
            Stopping keeps everything you wrote. It is not a delete, and nothing here says you gave
            up.
          </p>
        </section>
      )}
    </div>
  );
}

function BackLink(): JSX.Element {
  return (
    <Link className="linkish back-link" to="/">
      <ArrowLeftIcon /> Today
    </Link>
  );
}

/** One field, as it is answered. */
function AnswerField({
  field,
  value,
  onChange,
}: {
  field: HomeworkField;
  value: HomeworkValue;
  onChange: (v: HomeworkValue) => void;
}): JSX.Element {
  if (field.kind === 'paragraph') {
    return (
      <div className="field">
        <label htmlFor={`f-${field.id}`}>{field.label}</label>
        <textarea
          id={`f-${field.id}`}
          className="input"
          rows={4}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (field.kind === 'lines') {
    const lines = Array.isArray(value) ? value : [];
    return (
      <div className="field">
        <label htmlFor={`f-${field.id}-0`}>{field.label}</label>
        <div className="stack-sm stack">
          {Array.from({ length: field.count }, (_, i) => (
            <input
              key={i}
              id={`f-${field.id}-${i}`}
              className="input"
              aria-label={`${field.label}, line ${i + 1}`}
              value={lines[i] ?? ''}
              onChange={(e) => {
                const next = [...lines];
                while (next.length < field.count) next.push('');
                next[i] = e.target.value;
                onChange(next);
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (field.kind === 'scale') {
    const n = typeof value === 'number' ? value : null;
    return (
      <div className="field">
        <label htmlFor={`f-${field.id}`}>{field.label}</label>
        <RatingSlider
          id={`f-${field.id}`}
          value={n}
          onChange={(v) => onChange(v)}
          min={0}
          max={10}
          step={1}
          low={field.low || '0'}
          high={field.high || '10'}
        />
      </div>
    );
  }

  return (
    <div className="field">
      <span className="faint">{field.label}</span>
      <div className="chips">
        <button className="chip" aria-pressed={value === true} onClick={() => onChange(true)}>
          Yes
        </button>
        <button className="chip" aria-pressed={value === false} onClick={() => onChange(false)}>
          No
        </button>
      </div>
    </div>
  );
}

